/**
 * Push remoto iniciado pelo servidor — o mesmo recurso nas duas plataformas.
 *
 * O que existia antes: no web o `subscribeToPush()` chamava
 * `/api/notifications/subscribe` (rota inexistente — o router está montado em
 * `/api/polish`), o servidor não guardava nada e o `sw.js` não tinha listener
 * de `push`; no app o caminho nem começava, porque o WebView não tem service
 * worker nem PushManager. Ou seja: push remoto não funcionava em lugar nenhum,
 * só os avisos LOCAIS (agendados pelo próprio aparelho).
 *
 * Aqui as duas pontas se equivalem:
 *
 * | | Web | Android |
 * |---|---|---|
 * | Transporte | Web Push (VAPID) via service worker | FCM via `@capacitor/push-notifications` |
 * | Identificador | `endpoint` da subscription | token do FCM |
 * | Exibição com o app fechado | service worker (`sw.js`) | sistema, pelo bloco `notification` da mensagem |
 * | App aberto | evento `push` no SW | `pushNotificationReceived` |
 *
 * Os dois registram no mesmo endpoint (`POST /api/push/subscribe`) e o
 * servidor guarda o que é preciso para entregar (`server/push.ts`).
 *
 * Eventos de janela emitidos (o store de notificações escuta):
 * - `push:received` → aviso chegou com o jogo aberto
 * - `push:opened`   → jogador tocou no aviso
 */
import { Capacitor } from "@capacitor/core";
import { apiFetch, getClientPlatform } from "./apiBase";
import { getPlayerId } from "./utils";

export interface PushPreferencesPayload {
  types: Record<string, boolean>;
  quietHours: { enabled: boolean; start: string; end: string };
  tzOffsetMinutes: number;
}

export interface PushServerConfig {
  configured: boolean;
  webPush: boolean;
  fcm: boolean;
  publicKey: string | null;
}

/** Canal do Android; precisa bater com `FCM_CHANNEL_ID` do servidor. */
const CHANNEL_ID = "rocketanks_default";

type PushPlugin = typeof import("@capacitor/push-notifications").PushNotifications;

let _plugin: PushPlugin | null = null;
let _config: PushServerConfig | null = null;
let _listenersReady = false;

export function isNativePushSupported(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("PushNotifications");
  } catch {
    return false;
  }
}

export function isWebPushSupported(): boolean {
  if (Capacitor.isNativePlatform()) return false;
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
}

async function loadPlugin(): Promise<PushPlugin | null> {
  if (_plugin) return _plugin;
  if (!isNativePushSupported()) return null;
  try {
    const mod = await import("@capacitor/push-notifications");
    _plugin = mod.PushNotifications;
    return _plugin;
  } catch (error) {
    console.warn("[Push] Plugin nativo indisponível:", error);
    return null;
  }
}

/**
 * O servidor decide se o push remoto existe. Sem isso o navegador pediria
 * permissão de notificação para um canal que ninguém pode usar — e o `null`
 * do `publicKey` deixaria o `pushManager.subscribe()` estourar.
 */
export async function fetchPushConfig(force = false): Promise<PushServerConfig> {
  if (_config && !force) return _config;
  try {
    const response = await apiFetch("/api/push/config");
    if (!response.ok) throw new Error(`status ${response.status}`);
    _config = (await response.json()) as PushServerConfig;
  } catch {
    _config = { configured: false, webPush: false, fcm: false, publicKey: null };
  }
  return _config;
}

const emit = (name: string, detail: unknown) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

/**
 * A chave VAPID viaja em base64url; o `applicationServerKey` quer bytes.
 * Passar a string crua funciona em alguns navegadores e falha em outros —
 * converter aqui evita a diferença.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

async function sendSubscriptionToServer(body: Record<string, unknown>): Promise<boolean> {
  try {
    const response = await apiFetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-odys-id": getPlayerId() },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch (error) {
    console.warn("[Push] Falha ao registrar inscrição:", error);
    return false;
  }
}

// ============================================
// WEB (VAPID)
// ============================================

export async function registerWebPush(
  preferences: PushPreferencesPayload,
): Promise<string | null> {
  if (!isWebPushSupported()) return null;

  const config = await fetchPushConfig();
  if (!config.webPush || !config.publicKey) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    // Reaproveita a inscrição existente: `subscribe()` com a mesma chave
    // devolve a mesma, mas com chave diferente ele rejeita.
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.publicKey),
      }));

    const json = subscription.toJSON();
    const ok = await sendSubscriptionToServer({
      platform: "web",
      subscription: json,
      preferences,
    });
    return ok ? subscription.endpoint : null;
  } catch (error) {
    console.warn("[Push] Falha ao inscrever no Web Push:", error);
    return null;
  }
}

// ============================================
// ANDROID (FCM)
// ============================================

async function ensureNativeListeners(plugin: PushPlugin): Promise<void> {
  if (_listenersReady) return;
  _listenersReady = true;

  // Com o app aberto o Android NÃO desenha o aviso — quem trata é o app. Sem
  // este listener, um push recebido em primeiro plano simplesmente sumia.
  await plugin.addListener("pushNotificationReceived", (notification) => {
    emit("push:received", {
      title: notification.title,
      body: notification.body,
      data: notification.data ?? {},
    });
  });

  await plugin.addListener("pushNotificationActionPerformed", (action) => {
    emit("push:opened", {
      data: action.notification.data ?? {},
    });
  });

  await plugin.addListener("registrationError", (error) => {
    console.warn("[Push] Registro no FCM falhou:", error);
  });
}

/**
 * Registra no FCM e devolve o token. Resolve pelo evento `registration`, que é
 * assíncrono — daí a promessa com timeout em vez de um retorno direto.
 */
export async function registerNativePush(
  preferences: PushPreferencesPayload,
): Promise<string | null> {
  const plugin = await loadPlugin();
  if (!plugin) return null;

  const config = await fetchPushConfig();
  if (!config.fcm) return null;

  try {
    let permission = await plugin.checkPermissions();
    if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
      permission = await plugin.requestPermissions();
    }
    if (permission.receive !== "granted") return null;

    // O canal precisa existir antes da primeira mensagem: o `channel_id` que o
    // servidor manda em `buildFcmMessage()` aponta para ele.
    try {
      await plugin.createChannel({
        id: CHANNEL_ID,
        name: "Rocketanks",
        description: "Avisos do jogo",
        importance: 4,
        visibility: 1,
      });
    } catch {
      // iOS não tem canais; Android antigo tampouco. Não é motivo para parar.
    }

    await ensureNativeListeners(plugin);

    const token = await new Promise<string | null>((resolve) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(null);
        }
      }, 15_000);

      void plugin
        .addListener("registration", (registration) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          resolve(registration.value);
        })
        .then(() => plugin.register())
        .catch(() => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          resolve(null);
        });
    });

    if (!token) return null;

    const ok = await sendSubscriptionToServer({
      platform: getClientPlatform(),
      token,
      preferences,
    });
    return ok ? token : null;
  } catch (error) {
    console.warn("[Push] Falha ao registrar no FCM:", error);
    return null;
  }
}

// ============================================
// COMUM
// ============================================

/** Registra na plataforma certa. Devolve o identificador salvo no servidor. */
export async function registerPush(
  preferences: PushPreferencesPayload,
): Promise<string | null> {
  if (isNativePushSupported()) return registerNativePush(preferences);
  return registerWebPush(preferences);
}

export async function unregisterPush(endpoint: string | null): Promise<void> {
  if (endpoint) {
    try {
      await apiFetch("/api/push/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-odys-id": getPlayerId() },
        body: JSON.stringify({ endpoint }),
      });
    } catch {
      // Servidor fora do ar: a inscrição morre sozinha no primeiro 404/410
    }
  }

  if (isNativePushSupported()) {
    const plugin = await loadPlugin();
    try {
      await plugin?.unregister();
    } catch {
      // Nada a fazer se o plugin já esqueceu o registro
    }
    return;
  }

  if (!isWebPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    await subscription?.unsubscribe();
  } catch {
    // Idem: sem service worker não há o que cancelar
  }
}

/**
 * O filtro (tipo desligado, horário silencioso) roda no servidor, então ele
 * precisa da cópia atualizada sempre que o jogador mexe nas configurações.
 */
export async function syncPushPreferences(
  endpoint: string | null,
  preferences: PushPreferencesPayload,
): Promise<void> {
  if (!endpoint) return;
  try {
    await apiFetch("/api/push/preferences", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-odys-id": getPlayerId() },
      body: JSON.stringify({ endpoint, preferences }),
    });
  } catch {
    // Preferência é best-effort: o próximo registro reenvia tudo
  }
}

/** Dispara um push de teste no próprio jogador (tela de configurações). */
export async function sendTestPush(): Promise<boolean> {
  try {
    const response = await apiFetch("/api/push/test", {
      method: "POST",
      headers: { "x-odys-id": getPlayerId() },
    });
    return response.ok;
  } catch {
    return false;
  }
}
